import { ReactElement, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { FeeDefinition, feeDefinitionsCreateDeleteCr, feeDefinitionsValidateDelete } from '@pmp/api';
import { CrudMode } from '@ohpen/enso-ui';

import { FeesTabProps } from '../interfaces';
import { getInitialState, reducer } from '../reducer';
import { FeesList } from './FeesList';
import { FeesCrudForm } from './FeesCrudForm';
import { ConfirmCrDeleteModal } from '@pmp/components';

export const FeesTab = ({ valueLists, data, isFetching, fetchData }: FeesTabProps): ReactElement => {
  const [state, dispatch] = useReducer(reducer<FeeDefinition>, getInitialState<FeeDefinition>());
  const { t } = useTranslation();

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: FeeDefinition) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: FeeDefinition) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: FeeDefinition) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <>
      <FeesList
        data={data ?? []}
        isFetching={isFetching}
        valueLists={valueLists}
        onCreate={onCreate}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {state.crud.open && state.crud.mode && (
        <FeesCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          valueLists={valueLists}
          refreshTable={fetchData}
          onClose={onCloseCrud}
        />
      )}
      <ConfirmCrDeleteModal
        open={state.deleteModal.open}
        records={state.deleteModal.record ? [state.deleteModal.record] : []}
        idKey="feeId"
        nameKey="feeName"
        entityLabel={t('pmp.fees.labels.fee')}
        entityLabelPlural={t('pmp.fees.labels.fees')}
        getPrecheck={feeDefinitionsValidateDelete}
        createDeleteChangeRequest={feeDefinitionsCreateDeleteCr}
        refreshTable={fetchData}
        onClose={onCloseDeleteModal}
      />
    </>
  );
};
